/**
 * Created: 2026-04-07
 * Initial commit: Add event dialog for slot page event cell click (feat/event/mr-9)
 *
 * Dialog component for displaying event details and signed-up players
 * when an event-reserved merged cell is clicked in the SlotsPage.
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { dateFormat, message } from '@homemaster/jslib'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import {
  CancelEventDocument,
  EventStatus,
  GameFormat,
  type Gender,
  GetEventDocument,
  SportCategory,
} from '../gql/graphql'
import { formatEventTimeRange } from '../lib/dateUtils'
import {
  EVENT_STATUS_COLOR,
  EVENT_STATUS_LABEL,
  GAME_FORMAT_CHIP,
  GAME_FORMAT_LABEL,
} from '../lib/eventConstants'
import EventFormDialog from './EventFormDialog'
import UserAvatar from './UserAvatar'

// Created: 2026-04-07 | Initial commit: Add event dialog (feat/event/mr-9)
gql`
  query GetEvent($id: ID!) {
    event(id: $id) {
      ... on Event {
        id
        name
        location
        description
        startTime
        duration
        drawSize
        numSignups
        fee
        gameFormat
        status
        minRating
        maxRating
        images
        createTime
        creator {
          id
          name
          avatar
          gender
          openid
        }
        signups {
          id
          signupTime
          user {
            id
            name
            avatar
            mobile
            gender
            ratings {
              sport
              format
              rating
            }
            openid
          }
        }
        players {
          id
          rank
          user {
            id
            name
            avatar
            gender
            ratings {
              sport
              format
              rating
            }
            openid
          }
        }
        matches {
          id
          round
          match {
            id
            side1Score
            side2Score
            players {
              id
              side
              userId
            }
            sets {
              setNumber
              side1Score
              side2Score
              side1TiebreakPoints
              side2TiebreakPoints
            }
          }
        }
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }

  mutation CancelEvent($eventId: UUID!) {
    cancelEvent(eventId: $eventId) {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

interface PlayerStats {
  wins: number
  losses: number
  setsWon: number
}

function computePlayerStats(
  matches: Array<{
    match: {
      players: Array<{ userId: string; side: number }>
      side1Score?: number | null
      side2Score?: number | null
      sets: Array<{ side1Score: number; side2Score: number }>
    }
  }>,
): Map<string, PlayerStats> {
  const stats = new Map<string, PlayerStats>()
  for (const { match } of matches) {
    const { players, side1Score, side2Score, sets } = match
    if (side1Score == null || side2Score == null) continue
    const side1Won = side1Score > side2Score
    for (const p of players) {
      const existing = stats.get(p.userId) ?? { wins: 0, losses: 0, setsWon: 0 }
      const playerWon = (p.side === 1 && side1Won) || (p.side === 2 && !side1Won)
      const playerSetsWon = sets.reduce(
        (acc, set) => acc + (p.side === 1 ? set.side1Score : set.side2Score),
        0,
      )
      stats.set(p.userId, {
        wins: existing.wins + (playerWon ? 1 : 0),
        losses: existing.losses + (playerWon ? 0 : 1),
        setsWon: existing.setsWon + playerSetsWon,
      })
    }
  }
  return stats
}

interface MatchesTabProps {
  matches: Array<{
    id: string
    round: number
    match: {
      id: string
      side1Score?: number | null
      side2Score?: number | null
      players: Array<{ id: string; side: number; userId: string }>
      sets: Array<{
        setNumber: number
        side1Score: number
        side2Score: number
        side1TiebreakPoints?: number | null
        side2TiebreakPoints?: number | null
      }>
    }
  }>
  players: Array<{
    id: string
    user: {
      id?: string | null
      name?: string | null
      avatar?: string | null
      gender?: Gender | null
    }
  }>
}

function MatchesTab({ matches, players }: MatchesTabProps) {
  const userMap = new Map(players.map((p) => [p.user.id, p.user]))

  const sortedMatches = matches.slice().sort((a, b) => a.round - b.round)

  if (sortedMatches.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        暂无比赛记录
      </Typography>
    )
  }

  return (
    <List dense disablePadding>
      {sortedMatches.map(({ id, round, match }) => {
        const side1Players = match.players.filter((p) => p.side === 1)
        const side2Players = match.players.filter((p) => p.side === 2)
        const set = match.sets[0]
        const side1Won =
          match.side1Score != null &&
          match.side2Score != null &&
          match.side1Score > match.side2Score
        const side2Won =
          match.side1Score != null &&
          match.side2Score != null &&
          match.side2Score > match.side1Score

        return (
          <ListItem
            key={id}
            disableGutters
            sx={{ flexDirection: 'column', alignItems: 'stretch', mb: 1 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              第 {round} 轮
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
                px: 1,
                py: 0.5,
              }}
            >
              <Box sx={{ flex: 1 }}>
                {side1Players.map((p) => {
                  const u = userMap.get(p.userId)
                  return u ? <UserAvatar key={p.id} user={u} /> : null
                })}
              </Box>
              <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                {set ? (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
                  >
                    <Box
                      component="span"
                      sx={{ color: side1Won ? 'success.main' : 'text.secondary' }}
                    >
                      {set.side1Score}
                    </Box>
                    {set.side1TiebreakPoints != null && (
                      <Box component="sup" sx={{ fontSize: '0.65em' }}>
                        {set.side1TiebreakPoints}
                      </Box>
                    )}
                    {' : '}
                    <Box
                      component="span"
                      sx={{ color: side2Won ? 'success.main' : 'text.secondary' }}
                    >
                      {set.side2Score}
                    </Box>
                    {set.side2TiebreakPoints != null && (
                      <Box component="sup" sx={{ fontSize: '0.65em' }}>
                        {set.side2TiebreakPoints}
                      </Box>
                    )}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1, textAlign: 'right' }}>
                {side2Players.map((p) => {
                  const u = userMap.get(p.userId)
                  return u ? <UserAvatar key={p.id} user={u} /> : null
                })}
              </Box>
            </Box>
          </ListItem>
        )
      })}
    </List>
  )
}

interface RanksTabContentProps {
  players: Array<{
    id: string
    rank?: number | null
    user: {
      id?: string | null
      name?: string | null
      avatar?: string | null
      gender?: Gender | null
      ratings?: Array<{ sport: string; format: string; rating?: number | null }> | null
    }
  }>
  gameFormat: GameFormat
  matches: MatchesTabProps['matches']
}

function RanksTabContent({ players, gameFormat, matches }: RanksTabContentProps) {
  const stats = computePlayerStats(matches)

  const sorted = players.slice().sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))

  return (
    <List dense disablePadding>
      {sorted.map((player, idx) => {
        const s = stats.get(player.user.id ?? '') ?? { wins: 0, losses: 0, setsWon: 0 }
        return (
          <ListItem key={player.id} disableGutters>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 24, textAlign: 'right', mr: 1 }}
            >
              {idx + 1}.
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <UserAvatar user={player.user} />
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">
                  {gameFormat === GameFormat.RotatingDoubles
                    ? `获胜局数: ${s.setsWon} ${s.wins}胜 ${s.losses}负`
                    : `${s.wins}胜 ${s.losses}负 获胜局数: ${s.setsWon}`}
                </Typography>
              </Box>
            </Box>
          </ListItem>
        )
      })}
    </List>
  )
}

interface EventDialogProps {
  eventId: string
  onClose: () => void
}

export default function EventDialog({ eventId, onClose }: EventDialogProps) {
  const { data, loading } = useQuery(GetEventDocument, {
    variables: { id: eventId },
    fetchPolicy: 'network-only',
  })

  const [cancelEvent] = useMutation(CancelEventDocument)
  const [cancelling, setCancelling] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)

  const event = data?.event?.__typename === 'Event' ? data.event : null

  const isOver =
    !!event &&
    event.status !== EventStatus.Cancelled &&
    event.status !== EventStatus.Aborted &&
    event.status !== EventStatus.Completed &&
    new Date(event.startTime).getTime() + event.duration * 3600_000 < Date.now()

  const canCancel =
    event &&
    !isOver &&
    event.status !== EventStatus.Cancelled &&
    event.status !== EventStatus.Aborted &&
    event.status !== EventStatus.Completed &&
    (event.gameFormat === GameFormat.Freeplay || event.status === EventStatus.Created)

  const canEdit = event && !isOver && event.status === EventStatus.Created

  const handleCancel = () => {
    message.prompt(
      '取消活动后，所有已报名的用户将自动获得全额退款并收到短信通知。此操作不可撤销，请确认后再继续。',
      {
        title: '确认取消活动',
        noLabel: '返回',
        yesLabel: '确认取消',
        onYesButton: async () => {
          setCancelling(true)
          try {
            const result = await cancelEvent({
              variables: { eventId: eventId as string },
            })
            const data = result.data?.cancelEvent
            if (data?.__typename === 'GraphqlRequestError') {
              message.error(data.message ?? '取消失败')
            } else {
              message.success('活动已成功取消')
              onClose()
            }
          } finally {
            setCancelling(false)
          }
        },
      },
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 12 }}>
        {event && (
          <Chip
            label={GAME_FORMAT_CHIP[event.gameFormat].label}
            color={GAME_FORMAT_CHIP[event.gameFormat].color}
            size="small"
            sx={{ mr: 0.5, mb: 0.5 }}
          />
        )}
        {event?.name ?? '活动详情'}
        {event && (
          <Chip
            label={isOver ? '已结束' : EVENT_STATUS_LABEL[event.status]}
            size="small"
            color={isOver ? 'default' : EVENT_STATUS_COLOR[event.status]}
            sx={{ position: 'absolute', right: 44, top: 12 }}
          />
        )}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {event && (
          <>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  时间
                </Typography>
                <Typography variant="body2">
                  {formatEventTimeRange(event.startTime, event.duration)}
                </Typography>
              </Box>
              {event.gameFormat !== GameFormat.Freeplay ? (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    赛制
                  </Typography>
                  <Typography variant="body2">
                    {GAME_FORMAT_LABEL[event.gameFormat]}
                  </Typography>
                </Box>
              ) : (
                <Box />
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  地点
                </Typography>
                <Typography variant="body2">{event.location}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  报名费
                </Typography>
                <Typography variant="body2">
                  {Number(event.fee) === 0 ? '免费' : `¥${event.fee}`}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  报名人数
                </Typography>
                <Typography variant="body2">
                  {event.numSignups} / {event.drawSize}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  等级范围
                </Typography>
                <Typography variant="body2">
                  {event.minRating.toFixed(1)}–{event.maxRating.toFixed(1)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  创建人
                </Typography>
                <Box sx={{ mt: 0.25 }}>
                  <UserAvatar user={event.creator} showMobile={false} />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  创建时间
                </Typography>
                <Typography variant="body2">
                  {dateFormat(event.createTime, { format: 'yyyy-MM-dd HH:mm' })}
                </Typography>
              </Box>
            </Box>

            {event.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  活动描述
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {event.description}
                </Typography>
              </Box>
            )}

            <Divider sx={{ mb: 1 }} />

            {event.status === EventStatus.Completed &&
            event.gameFormat !== GameFormat.Freeplay ? (
              <>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  sx={{ mb: 1 }}
                >
                  <Tab label="报名选手" />
                  <Tab label="比赛结果" />
                  <Tab label="最终名次" />
                </Tabs>

                {activeTab === 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      已报名选手 ({event.signups.length})
                    </Typography>
                    {event.signups.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        暂无选手报名
                      </Typography>
                    ) : (
                      <List dense disablePadding>
                        {event.signups.map((signup, idx) => (
                          <ListItem key={signup.id} disableGutters>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ minWidth: 24, textAlign: 'right', mr: 1 }}
                            >
                              {idx + 1}.
                            </Typography>
                            <Box
                              sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                              <UserAvatar user={signup.user} />
                              <Box
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                              >
                                {signup.user.ratings?.find(
                                  (r) =>
                                    r.sport === SportCategory.Tennis &&
                                    r.format === GameFormat.Singles,
                                )?.rating != null && (
                                  <Typography variant="caption" color="text.secondary">
                                    {signup.user.ratings
                                      .find(
                                        (r) =>
                                          r.sport === SportCategory.Tennis &&
                                          r.format === GameFormat.Singles,
                                      )
                                      ?.rating?.toFixed(2)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </>
                )}

                {activeTab === 1 && (
                  <MatchesTab matches={event.matches} players={event.players} />
                )}

                {activeTab === 2 && (
                  <RanksTabContent
                    players={event.players}
                    gameFormat={event.gameFormat}
                    matches={event.matches}
                  />
                )}
              </>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  已报名选手 ({event.signups.length})
                </Typography>
                {event.signups.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    暂无选手报名
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {event.signups.map((signup, idx) => (
                      <ListItem key={signup.id} disableGutters>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ minWidth: 24, textAlign: 'right', mr: 1 }}
                        >
                          {idx + 1}.
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <UserAvatar user={signup.user} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {signup.user.ratings?.find(
                              (r) =>
                                r.sport === SportCategory.Tennis &&
                                r.format === GameFormat.Singles,
                            )?.rating != null && (
                              <Typography variant="caption" color="text.secondary">
                                {signup.user.ratings
                                  .find(
                                    (r) =>
                                      r.sport === SportCategory.Tennis &&
                                      r.format === GameFormat.Singles,
                                  )
                                  ?.rating?.toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      {(canEdit || canCancel) && (
        <DialogActions>
          {canEdit && (
            <Button variant="outlined" onClick={() => setEditDialogOpen(true)}>
              编辑
            </Button>
          )}
          {canCancel && (
            <Button
              variant="contained"
              color="error"
              onClick={handleCancel}
              disabled={cancelling}
            >
              取消活动
            </Button>
          )}
        </DialogActions>
      )}
      {editDialogOpen && event && (
        <EventFormDialog
          slots={[]}
          editEvent={event}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={() => {
            setEditDialogOpen(false)
            // Apollo automatically updates the cache with the returned Event
          }}
        />
      )}
    </Dialog>
  )
}
